import React, {
    useCallback,
    forwardRef,
    ReactElement,
    Ref,
    useState,
    useEffect,
  } from "react";
  import moment from "moment";
  import { useDispatch, useSelector } from "react-redux";
  import { AppState } from "../../config/redux/reducers";
  
  //common components
  import CustomDialog from "../../common/Dialog";
  import SelectBox from "../../common/SelectBox";
  import TextBox from "../../common/TextBox";
  import DateRangePickerComponent from "../../common/DateRangeComponent";
  
  //Material UI Components
  import { useStyles } from "../styles/BaseEdit.styles";
  import { TransitionProps } from "@material-ui/core/transitions";
  import Slide from "@material-ui/core/Slide";
  import { Grid, Typography, Button } from "@material-ui/core";
  
  //constants
  import AppConstants from "../../constants";
  
  //Assets
  import warning from "../../assets/icons/warning.svg";
  import { transformSlotValue } from "../../utils/helpers.utils";
  import DeliveryConstants from "constants/SharedConstants/DeliveryConstants";
  
  interface ValetTrolleyOrderDetailsEditProps {
    open: boolean;
    consignment: any;
    isExpress?: any;
    closePopup: () => void;
    reduxStates: {
      statuses: any[];
      deliveryReasons: any[];
      reasonsDropdownList: any[];
      replanningConsignmentSuccess: boolean;
      consignmentDataAfterReplanning: any;
      deliverySlotList: any;
    };
    transforms: {
      createOrderReplanningPayload: (params: any) => any;
    };
    actions: {
      orderReplanning: (params: any) => void;
      fetchStatuses: (params: any) => void;
      fetchDeliveryReasons: () => void;
    };
  }
  
  const Transition = forwardRef(function Transition(
    props: TransitionProps & { children?: ReactElement<any, any> },
    ref: Ref<unknown>
  ) {
    return <Slide direction="left" ref={ref} {...props} />;
  });
  
  const OrderDetailsEdit = (
    props: ValetTrolleyOrderDetailsEditProps
  ) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { open, consignment, closePopup, reduxStates, transforms, actions } = props;
  
    const {
      statuses,
      deliveryReasons,
      reasonsDropdownList,
      replanningConsignmentSuccess,
      consignmentDataAfterReplanning,
      deliverySlotList,
    } = reduxStates;

    const { orderReplanning, fetchStatuses, fetchDeliveryReasons } = actions;

   const { createOrderReplanningPayload } = transforms

    const { countryCode } = useSelector((state: AppState) => state.common);
    const [selectedStatus, setSelectedStatus] = useState(
      DeliveryConstants.CONSIGNMENTS_DATA.DEFAULT_REPLANNING_SELECTION
    );
    const [selectedSlot, setSelectedSlot] = useState(
      AppConstants.SELECT_NONE.value
    );
    const [selectedReason, setSelectedReason] = useState(
      AppConstants.SELECT_NONE.value
    );
    const [selectedDate, setSelectedDate] = useState({
      startDate: moment()
        .hours(0)
        .minutes(0)
        .seconds(0)
        .format(AppConstants.DATE_FORMAT_UI),
      endDate: moment()
        .hours(23)
        .minutes(59)
        .seconds(59)
        .format(AppConstants.DATE_FORMAT_UI),
    });
    const [showReason, setShowReason] = useState(false);
    const [commentsValue, setCommentsValue] = useState("");
    const [errors, setErrors] = useState({
      reason: "",
    });
  
    let deliveryInformation: any = {};
    let metadata: any = {};
    let deliveryDate = "";
  
    if (consignment) {
      deliveryInformation = consignment.deliveryInformation;
      metadata = consignment.metadata;
      if (deliveryInformation && deliveryInformation.deliverySlot) {
        deliveryDate = moment(
          deliveryInformation.deliverySlot.startTime,
          AppConstants.DATE_FORMAT_BACKEND
        ).format(AppConstants.DATE_FORMAT);
      }
    }
    const dialogPaperProps = {
      classes: {
        root: classes.dialogPaperPropsRoot,
      },
      square: true,
    };
  
    const getStatusField = (fieldName: string, statusCode: string) => {
      const statuses: any = DeliveryConstants.CONSIGNMENTS_DATA.STATUSES;
      return statusCode && statuses[statusCode]
        ? statuses[statusCode][fieldName]
        : "";
    };
  
    const validateForm = useCallback(() => {
      let newErrors = {};
      if (selectedReason === AppConstants.SELECT_NONE.value) {
        newErrors = { ...newErrors, reason: "Please select reason" };
      }
      setErrors({ ...errors, ...newErrors });
  
      return Object.keys(newErrors).length;
    }, [errors, selectedReason]);
  
    const handleSlotChange = useCallback((value: any) => {
      setSelectedSlot(value);
    }, []);
  
    const handleReasonChange = useCallback(
      (value: any) => {
        setSelectedReason(value);
        if (value) {
          setErrors({ ...errors, reason: "" });
        }
      },
      [errors]
    );
  
    const handleCommentsTextChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setCommentsValue(event.target.value);
      },
      []
    );
  
    const handleDateChange = useCallback((value: any) => {
      setSelectedDate(value);
    }, []);
  
    const handleVTOrderReplanning = useCallback(() => {
      if (
        selectedStatus !==
        DeliveryConstants.CONSIGNMENTS_DATA.DEFAULT_REPLANNING_SELECTION
      ) {
        if (selectedStatus === DeliveryConstants.CONSIGNMENTS_DATA.UNDELIVERED) {
          if (validateForm() === 0) {
            const payload = createOrderReplanningPayload({
              id: consignment.consignmentCode,
              selection: selectedStatus,
              reason: selectedReason,
              commentsValue: commentsValue,
            });
            if (selectedReason !== AppConstants.SELECT_NONE.value) {
              const deliveryReason = deliveryReasons.filter(
                (reason: any) => reason.reasonCode === selectedReason
              );
              if (deliveryReason.length) {
                payload.reason = deliveryReason[0];
              }
            }
            dispatch(
              orderReplanning({
                params: payload,
              })
            );
          }
        } else {
          const payload = createOrderReplanningPayload({
            id: consignment.consignmentCode,
            commentsValue: commentsValue,
            selection: selectedStatus,
          });
          dispatch(
            orderReplanning({
              params: payload,
            })
          );
        }
      } else {
        const payload = createOrderReplanningPayload({
          id: consignment.consignmentCode,
          status: selectedStatus,
          slot: selectedSlot,
          date: selectedDate,
        });
        dispatch(
          orderReplanning({
            params: payload,
          })
        );
      }
    }, [
      consignment,
      selectedStatus,
      selectedDate,
      selectedSlot,
      commentsValue,
      deliveryReasons,
      selectedReason,
      validateForm,
      dispatch,
    ]);
  
    const dialogActionHandler = useCallback(
      (type: string) => {
        if (type === "update") {
          handleVTOrderReplanning();
        } else {
          closePopup();
        }
      },
      [closePopup, handleVTOrderReplanning]
    );
  
    const handleStatusChange = useCallback((value: any) => {
      setSelectedStatus(value);
      if (value === DeliveryConstants.CONSIGNMENTS_DATA.UNDELIVERED) {
        setShowReason(true);
        setSelectedReason(AppConstants.SELECT_NONE.value);
      } else {
        setShowReason(false);
        setSelectedReason(AppConstants.SELECT_NONE.value);
      }
    }, []);
  
    const fetchAllDeliveryReasons = useCallback(() => {
      dispatch(fetchDeliveryReasons());
    }, [dispatch]);
  
    const handleVTStatuses = useCallback(
      (consignment: any) => {
        if (Object.keys(consignment).length) {
          const consignmentCode = consignment && consignment.consignmentCode;
          dispatch(
            fetchStatuses({
              params: consignmentCode,
            })
          );
        }
      },
      [dispatch]
    );
  
    useEffect(() => {
      if (replanningConsignmentSuccess) {
        closePopup();
      }
    }, [replanningConsignmentSuccess]);
  
    /*
     * Called on page load, to set the value in the delivery slot from the constants which is amongst the delivery slot list.
     * If the order contains a delivery slot value which matches the value from the deliverySlotListCHD, sets that as a preselected
     * value
     */
    useEffect(() => {
      if (deliveryInformation && deliverySlotList) {
        setSelectedSlot(
          deliverySlotList[
            sessionStorage.getItem(AppConstants.COUNTRY_CODE) || countryCode
          ][0].value
        );
        deliverySlotList[
          sessionStorage.getItem(AppConstants.COUNTRY_CODE) || countryCode
        ].forEach((slot: any) => {
          if (
            transformSlotValue(
              deliveryInformation.deliverySlot.deliverySlotTime
            ) === transformSlotValue(slot.value)
          ) {
            setSelectedSlot(
              deliveryInformation &&
                deliveryInformation.deliverySlot.deliverySlotTime
            );
          }
        });
      }
    }, [consignment, countryCode]);
  
    useEffect(() => {
      if (consignment) {
        setSelectedDate({
          startDate: moment(
            deliveryDate ? deliveryDate : new Date(),
            AppConstants.DATE_FORMAT
          )
            .hours(0)
            .minutes(0)
            .seconds(0)
            .format(AppConstants.DATE_FORMAT_UI),
          endDate: moment(
            deliveryDate ? deliveryDate : new Date(),
            AppConstants.DATE_FORMAT
          )
            .hours(23)
            .minutes(59)
            .seconds(59)
            .format(AppConstants.DATE_FORMAT_UI),
        });
        setCommentsValue("");
        setShowReason(false);
        setSelectedReason(AppConstants.SELECT_NONE.value);
        setSelectedStatus(
          DeliveryConstants.CONSIGNMENTS_DATA.DEFAULT_REPLANNING_SELECTION
        );
      }
    }, [consignment]);
  
    useEffect(() => {
      if (consignment != null) {
        fetchAllDeliveryReasons();
        handleVTStatuses(consignment);
      }
    }, [consignment, fetchAllDeliveryReasons, handleVTStatuses]);
  
    const getTitleContent = () => {
      return (
        <Grid className={classes.titleContainer} container>
          <Grid item>
            <Grid className="headingContainer" container>
              <Grid item>
                <Grid container alignItems="center">
                  <Grid item>
                    <Typography className="titleHeading">
                      <span className="label"> EDIT CONSIGNMENT : </span>
                      <span className="content">
                        {" "}
                        {consignment.consignmentCode}{" "}
                      </span>
                    </Typography>
                  </Grid>
                  {consignmentDataAfterReplanning &&
                  consignmentDataAfterReplanning.metadata ? (
                    <Grid className="tagContainer" item>
                      <Typography
                        className={[
                          "tag",
                          getStatusField(
                            "color",
                            consignmentDataAfterReplanning &&
                              consignmentDataAfterReplanning.metadata &&
                              consignmentDataAfterReplanning.metadata
                                .deliveryStatus
                          ),
                        ].join(" ")}
                      >
                        {consignmentDataAfterReplanning &&
                        consignmentDataAfterReplanning.metadata &&
                        consignmentDataAfterReplanning.metadata.deliveryStatus
                          ? consignmentDataAfterReplanning.metadata.deliveryStatus
                          : ""}
                      </Typography>
                    </Grid>
                  ) : (
                    metadata &&
                    metadata.deliveryStatus && (
                      <Grid className="tagContainer" item>
                        <Typography
                          className={[
                            "tag",
                            getStatusField(
                              "color",
                              metadata && metadata.deliveryStatus
                            ),
                          ].join(" ")}
                        >
                          {metadata && metadata.deliveryStatus
                            ? metadata.deliveryStatus
                            : ""}
                        </Typography>
                      </Grid>
                    )
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    };
  
    const getDetailsContent = () => {
      return (
        <Grid className={classes.contentContainer} container>
          <Grid className="selectedContentArea" item>
            <Grid className="contentAreaContainer cancel" container>
              <Grid className="contentAreaItem" item>
                <Grid className="itemContainer" container>
                  <Grid className="itemLabel" item>
                    <Typography>Update Status</Typography>
                  </Grid>
                  <Grid className="itemInput" item>
                    <SelectBox
                      fullWidth
                      value={selectedStatus}
                      id="vtStatus"
                      inputProps={{
                        name: "status",
                        id: "vtStatus",
                      }}
                      required
                      handleChange={handleStatusChange}
                      items={statuses || []}
                    ></SelectBox>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid className="selectedContentArea" item>
            {selectedStatus ===
            DeliveryConstants.CONSIGNMENTS_DATA.DEFAULT_REPLANNING_SELECTION ? (
              <Grid className="contentAreaContainer reschedule" container>
                <Grid className="contentAreaItem" item>
                  <Grid className="itemContainer" container>
                    <Grid className="itemLabel" item>
                      <Typography>Delivery Date</Typography>
                    </Grid>
                    <Grid className="itemInput" item>
                      <DateRangePickerComponent
                        fullWidth
                        keyId="EditConsignmentDeliveryDate"
                        placeholder="Delivery Date"
                        value={selectedDate}
                        isTextField={true}
                        autoUpdateInput={true}
                        autoApply={true}
                        maxSpan={{
                          day: "0",
                        }}
                        showDropdowns={true}
                        linkedCalendars={true}
                        locale={{ format: AppConstants.DATE_FORMAT_UI }}
                        updateDateRange={handleDateChange}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid className="contentAreaItem" item>
                  <Grid className="itemContainer" container>
                    <Grid className="itemLabel" item>
                      <Typography>Slot</Typography>
                    </Grid>
                    <Grid className="itemInput" item>
                      <SelectBox
                        fullWidth
                        value={selectedSlot}
                        id="EditConsignmentSlot"
                        inputProps={{
                          name: "slot",
                          id: "EditConsignmentSlot",
                        }}
                        handleChange={handleSlotChange}
                        items={
                          deliverySlotList[
                            sessionStorage.getItem(AppConstants.COUNTRY_CODE) ||
                              countryCode
                          ]
                        }
                      ></SelectBox>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            ) : (
              <Grid className="contentAreaContainer cancel" container>
                {showReason ? (
                  <Grid className="contentAreaItem" item>
                    <Grid className="itemContainer" container>
                      <Grid className="itemLabel" item>
                        <Typography>Reason</Typography>
                      </Grid>
                      <Grid className="itemInput" item>
                        <SelectBox
                          fullWidth
                          value={selectedReason}
                          id="EditConsignmentReason"
                          inputProps={{
                            name: "reason",
                            id: "EditConsignmentReason",
                          }}
                          required
                          handleChange={handleReasonChange}
                          items={reasonsDropdownList || []}
                          error={!!errors.reason}
                          helperText={errors.reason}
                        ></SelectBox>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : null}
                <Grid className="contentAreaItem" item>
                  <Grid className="itemContainer" container>
                    <Grid className="itemLabel" item>
                      <Typography>Comments</Typography>
                    </Grid>
                    <Grid className="itemInput" item>
                      <TextBox
                        fullWidth
                        multiline
                        variant="outlined"
                        rows={4}
                        onChange={handleCommentsTextChange}
                        value={commentsValue}
                        textBoxId="EditConsignmentComments"
                        placeholderText="Enter a comment"
                      ></TextBox>
                    </Grid>
                  </Grid>
                </Grid>
                {showReason ? (
                  <Grid
                    className="contentAreaItem"
                    item
                    style={{
                      height: "55px",
                      background: "#E79832",
                      borderRadius: "8px",
                    }}
                  >
                    <Grid style={{ display: "flex", margin: "16px" }}>
                      <img src={warning} alt="warning" />
                      <Typography
                        style={{
                          fontSize: "14px",
                          marginLeft: "10px",
                          lineHeight: "17px",
                          fontWeight: 500,
                          color: "#FFFFFF",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        This consignment will no longer be delivered.
                      </Typography>
                    </Grid>
                  </Grid>
                ) : null}
              </Grid>
            )}
          </Grid>
        </Grid>
      );
    };
    const getDialogActions = () => {
      return (
        <Grid className={classes.buttons} container>
          <Button
            className="userBtn secondary"
            variant="contained"
            onClick={() => {
              dialogActionHandler("cancel");
            }}
          >
            {AppConstants.BUTTONS.CANCEL}
          </Button>
          <Button
            className="userBtn primary"
            variant="contained"
            onClick={() => {
              dialogActionHandler("update");
            }}
          >
            {AppConstants.BUTTONS.SAVE}
          </Button>
        </Grid>
      );
    };
  
    const handleDialogClose = useCallback(() => {
      closePopup();
    }, [closePopup]);
  
    return (
      <CustomDialog
        open={open}
        TransitionComponent={Transition}
        PaperProps={dialogPaperProps}
        title={getTitleContent()}
        content={getDetailsContent()}
        actions={getDialogActions()}
        handleClose={handleDialogClose}
      ></CustomDialog>
    );
  };
  
  export default OrderDetailsEdit;
  